import { useEffect, useState } from 'react';
import {
  writeImpersonationSession,
  clearImpersonationSession,
  isImpersonating,
  getAdminOrigin,
  isAllowedAdminOrigin,
  type ImpersonationPayload,
} from '../utils/impersonationSession';

export function useImpersonationListener() {
  const [active, setActive] = useState(isImpersonating);

  useEffect(() => {
    // Signal admin tab that this tab is ready to receive the payload.
    // Only send if not already impersonating (AuthChecker sends the initial READY;
    // this avoids a spurious second READY once the Layout mounts post-handshake).
    if (window.opener && !isImpersonating()) {
      try {
        const adminOrigin = getAdminOrigin() || '*';
        window.opener.postMessage({ type: 'IMPERSONATION_READY' }, adminOrigin);
      } catch {
        // opener may be cross-origin restricted; handled gracefully
      }
    }

    const handler = (e: MessageEvent) => {
      if (!isAllowedAdminOrigin(e.origin)) return;

      if (e.data?.type === 'IMPERSONATION_START') {
        const {
          accessToken,
          refreshToken,
          userId,
          userEmail,
          userRole,
          userFirstName,
          userLastName,
          companyName,
          adminName,
        } = e.data as {
          type: string;
          accessToken: string;
          refreshToken: string;
          userId: string;
          userEmail: string;
          userRole: string;
          userFirstName?: string;
          userLastName?: string;
          companyName?: string;
          adminName?: string;
        };
        const sessionPayload: ImpersonationPayload = {
          accessToken,
          refreshToken,
          userId,
          userEmail,
          userRole,
        };
        if (userFirstName !== undefined) sessionPayload.userFirstName = userFirstName;
        if (userLastName !== undefined) sessionPayload.userLastName = userLastName;
        if (companyName !== undefined) sessionPayload.companyName = companyName;
        if (adminName !== undefined) sessionPayload.adminName = adminName;

        writeImpersonationSession(sessionPayload);
        setActive(true);
        return;
      }

      if (e.data?.type === 'END_IMPERSONATION_FROM_ADMIN') {
        if (e.data?.reason === 'SESSION_EXPIRED') {
          clearImpersonationSession();
          setActive(false);
          window.close();
          return;
        }
        if (window.opener) {
          const adminOrigin = getAdminOrigin() || e.origin;
          window.opener.postMessage({ type: 'END_IMPERSONATION_REQUEST' }, adminOrigin);
        }
        return;
      }

      if (e.data?.type === 'END_IMPERSONATION_CONFIRMED') {
        clearImpersonationSession();
        setActive(false);
        window.close();
        return;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return active;
}
